import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LABEL_COLOURS,
  type CardLabel,
  type Label,
  type LabelColour,
} from '@molt/shared';
import { Plus, Search, Trash2, X } from 'lucide-react';
import {
  Button,
  EmptyState,
  FacetOption,
  IconButton,
  LabelChip,
  Popover,
  TextField,
} from '@/components';
import { labelDisplayName } from '@/lib/label-display';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  boardLabelsQueryOptions,
  useApplyCardLabel,
  useCreateLabel,
  useDeleteLabel,
  useRecolourLabel,
  useRemoveCardLabel,
  useRenameLabel,
} from '@/lib/labels';
import {
  appearancePreferencesQueryOptions,
  useSetColourBlindMode,
} from '@/lib/appearance';
import { labelColourDisplayName } from '../palette';

export interface LabelPickerProps {
  boardId: string;
  cardId: string;
  cardLabels: readonly CardLabel[];
  canEdit: boolean;
  open: boolean;
  onFilterByLabel?: ((labelId: string) => void) | undefined;
  onOpenChange: (open: boolean) => void;
  onToggleLabelSuccess?:
    | ((change: { readonly labelId: string; readonly selected: boolean }) => void)
    | undefined;
}

const DEFAULT_NEW_COLOUR: LabelColour = 'green';

function labelIds(labels: readonly CardLabel[]): Set<string> {
  return new Set(labels.map((label) => label.id));
}

/**
 * The label's stored name remains literal data; the fixed colour vocabulary
 * follows the interface catalogue so the visible option and the search haystack
 * speak the same language.
 */
function labelMatches(label: Label, query: string, colourName: string): boolean {
  const haystack = `${label.name ?? ''} ${colourName}`.toLocaleLowerCase();
  return haystack.includes(query);
}

export function LabelPicker({
  boardId,
  cardId,
  cardLabels,
  canEdit,
  open,
  onFilterByLabel,
  onOpenChange,
  onToggleLabelSuccess,
}: LabelPickerProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const labels = useQuery(boardLabelsQueryOptions(boardId));
  const appearance = useQuery(appearancePreferencesQueryOptions());
  const applyLabel = useApplyCardLabel();
  const removeLabel = useRemoveCardLabel();
  const createLabel = useCreateLabel();
  const renameLabel = useRenameLabel();
  const recolourLabel = useRecolourLabel();
  const deleteLabel = useDeleteLabel();
  const setColourBlindMode = useSetColourBlindMode();

  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [newColour, setNewColour] = useState<LabelColour>(DEFAULT_NEW_COLOUR);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColour, setEditingColour] = useState<LabelColour>(DEFAULT_NEW_COLOUR);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const confirmDeleteButtonRef = useRef<HTMLButtonElement | null>(null);

  const selectedIds = useMemo(() => labelIds(cardLabels), [cardLabels]);
  const filteredLabels = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const all = labels.data ?? [];
    if (query === '') return all;
    return all.filter((label) =>
      labelMatches(label, query, labelColourDisplayName(t, label.colour)),
    );
  }, [labels.data, search, t]);

  const busy =
    applyLabel.isPending ||
    removeLabel.isPending ||
    createLabel.isPending ||
    renameLabel.isPending ||
    recolourLabel.isPending ||
    deleteLabel.isPending;
  const colourBlindMode = appearance.data?.colourBlindMode ?? false;

  useEffect(() => {
    if (confirmingDeleteId === null) return;
    confirmDeleteButtonRef.current?.focus();
  }, [confirmingDeleteId]);

  function toggleLabel(label: Label) {
    if (busy) return;
    const variables = { boardId, cardId, labelId: label.id };
    if (selectedIds.has(label.id)) {
      removeLabel.mutate(variables, {
        onSuccess: () => onToggleLabelSuccess?.({ labelId: label.id, selected: false }),
      });
    } else {
      applyLabel.mutate(variables, {
        onSuccess: () => onToggleLabelSuccess?.({ labelId: label.id, selected: true }),
      });
    }
  }

  function beginEdit(label: Label) {
    setEditingId(label.id);
    setEditingName(label.name ?? '');
    setEditingColour(label.colour);
    setConfirmingDeleteId(null);
  }

  async function saveEdit(label: Label) {
    const trimmed = editingName.trim();
    const nextName = trimmed === '' ? null : trimmed;
    try {
      if (nextName !== label.name) {
        await renameLabel.mutateAsync({ boardId, labelId: label.id, name: nextName });
      }
      if (editingColour !== label.colour) {
        await recolourLabel.mutateAsync({
          boardId,
          labelId: label.id,
          colour: editingColour,
        });
      }
      setEditingId(null);
    } catch {
      // The mutation hooks announce the failure and keep the editor open.
    }
  }

  function createNewLabel() {
    createLabel.mutate(
      {
        boardId,
        body: { name: newName.trim() === '' ? null : newName, colour: newColour },
      },
      {
        onSuccess: () => {
          setNewName('');
          setNewColour(DEFAULT_NEW_COLOUR);
        },
      },
    );
  }

  async function confirmDelete(label: Label) {
    try {
      await deleteLabel.mutateAsync({ boardId, labelId: label.id });
      setConfirmingDeleteId(null);
    } catch {
      // The mutation hook announces the failure and leaves confirmation visible.
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      title={t('labelPicker.title')}
      width={360}
      trigger={<Button size="sm">{t('labelPicker.trigger')}</Button>}
      closeControl={
        <IconButton label={t('labelPicker.close')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      search={
        <div className="flex flex-col gap-2">
          <TextField
            label={t('labelPicker.search')}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            clearable
            onClear={() => setSearch('')}
            wrapperClassName="gap-1"
          />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={colourBlindMode}
              disabled={appearance.isLoading || setColourBlindMode.isPending}
              onChange={(event) => setColourBlindMode.mutate(event.currentTarget.checked)}
              className="size-4 rounded border-border-strong accent-accent"
            />
            <span>{t('labelPicker.colourBlindMode')}</span>
          </label>
        </div>
      }
      footer={
        canEdit ? (
          <div className="flex flex-col gap-2">
            <TextField
              label={t('labelPicker.newName')}
              value={newName}
              onChange={(event) => setNewName(event.currentTarget.value)}
              placeholder={t('labelPicker.newNamePlaceholder')}
              wrapperClassName="gap-1"
            />
            <div className="flex items-end gap-2">
              <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-medium text-ink">
                {t('labelPicker.colour')}
                <select
                  value={newColour}
                  aria-label={t('labelPicker.newColour')}
                  onChange={(event) =>
                    setNewColour(event.currentTarget.value as LabelColour)
                  }
                  className="h-9 rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink"
                >
                  {LABEL_COLOURS.map((colour) => (
                    <option key={colour} value={colour}>
                      {labelColourDisplayName(t, colour)}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                size="sm"
                variant="primary"
                className="shrink-0 whitespace-nowrap"
                busy={createLabel.isPending}
                onClick={createNewLabel}
                iconStart={<Plus aria-hidden="true" className="size-4" />}
              >
                {t('labelPicker.create')}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      {labels.isError ? (
        <EmptyState
          tone="error"
          size="inline"
          title={t('labelPicker.loadFailedTitle')}
          action={
            <Button size="sm" onClick={() => void labels.refetch()}>
              {t('labelPicker.retry')}
            </Button>
          }
        />
      ) : labels.data === undefined ? (
        <p className="text-sm text-ink-muted">{t('labelPicker.loading')}</p>
      ) : filteredLabels.length === 0 ? (
        <EmptyState tone="neutral" size="inline" title={t('labelPicker.noMatch')} />
      ) : (
        <div className="flex flex-col gap-1">
          {filteredLabels.map((label) => {
            const selected = selectedIds.has(label.id);
            const editing = editingId === label.id;
            const confirming = confirmingDeleteId === label.id;
            /**
             * ⚠ FE-80.1 — the label's OWN name, not interface text. It is the
             * `{label}` slot for the eight routed row actions below, and it is
             * `labelChipName`'s output, which for a name-less label is the
             * English `'{Colour} label, no name'` authored in `LabelChip.tsx`.
             * That frame and the 31 colour words in `palette.ts` are the
             * largest unrouted English surface this file reaches, and they are
             * deliberately NOT taken here — `palette.test.ts` pins all 31
             * against the live `ck_labels_colour` member list, `LabelChip.tsx`
             * and `BoardMapView.tsx:466` consume them, and that is a slice of
             * its own.
             */
            const displayName = labelDisplayName(label, t);
            return (
              <div
                key={label.id}
                className="rounded-card border border-border bg-surface px-2 py-2"
              >
                {editing ? (
                  <div className="flex flex-col gap-2">
                    <TextField
                      label={t('labelPicker.nameFor', { label: displayName })}
                      value={editingName}
                      onChange={(event) => setEditingName(event.currentTarget.value)}
                      wrapperClassName="gap-1"
                    />
                    <label className="flex flex-col gap-1 text-sm font-medium text-ink">
                      {t('labelPicker.colour')}
                      <select
                        value={editingColour}
                        aria-label={t('labelPicker.colourFor', {
                          label: displayName,
                        })}
                        onChange={(event) =>
                          setEditingColour(event.currentTarget.value as LabelColour)
                        }
                        className="h-9 rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink"
                      >
                        {LABEL_COLOURS.map((colour) => (
                          <option key={colour} value={colour}>
                            {labelColourDisplayName(t, colour)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        aria-label={t('labelPicker.saveNamed', {
                          label: displayName,
                        })}
                        busy={renameLabel.isPending || recolourLabel.isPending}
                        onClick={() => void saveEdit(label)}
                      >
                        {t('labelPicker.save')}
                      </Button>
                      <Button
                        size="sm"
                        variant="quiet"
                        aria-label={t('labelPicker.cancelEdit', {
                          label: displayName,
                        })}
                        onClick={() => setEditingId(null)}
                      >
                        {t('labelPicker.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FacetOption
                      control="checkbox"
                      label={displayName}
                      checked={selected}
                      disabled={!canEdit}
                      aria-disabled={busy || undefined}
                      onChange={() => toggleLabel(label)}
                      leadingKind="label-chip"
                      leading={
                        <LabelChip colour={label.colour} name={label.name} size="sm" />
                      }
                      className="min-w-0 flex-1 px-1 py-1"
                    />
                    {selected && onFilterByLabel !== undefined ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="quiet"
                        className="shrink-0 whitespace-nowrap px-1.5"
                        iconStart={<Search className="size-3" />}
                        aria-label={t('labelPicker.filterNamed', {
                          label: displayName,
                        })}
                        onClick={() => onFilterByLabel(label.id)}
                      >
                        {t('labelPicker.filter')}
                      </Button>
                    ) : null}
                    {canEdit ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          size="sm"
                          variant="quiet"
                          aria-label={t('labelPicker.editNamed', {
                            label: displayName,
                          })}
                          onClick={() => beginEdit(label)}
                        >
                          {t('labelPicker.edit')}
                        </Button>
                        {confirming ? (
                          <div className="flex items-center gap-1">
                            <Button
                              ref={confirmDeleteButtonRef}
                              size="sm"
                              variant="primary"
                              busy={deleteLabel.isPending}
                              aria-label={t('labelPicker.deleteNamed', {
                                label: displayName,
                              })}
                              onClick={() => void confirmDelete(label)}
                            >
                              {t('labelPicker.delete')}
                            </Button>
                            <Button
                              size="sm"
                              variant="quiet"
                              aria-label={t('labelPicker.cancelDelete', {
                                label: displayName,
                              })}
                              onClick={() => setConfirmingDeleteId(null)}
                            >
                              {t('labelPicker.cancel')}
                            </Button>
                          </div>
                        ) : (
                          <IconButton
                            label={t('labelPicker.deleteNamed', {
                              label: displayName,
                            })}
                            size="sm"
                            onClick={() => setConfirmingDeleteId(label.id)}
                          >
                            <Trash2 aria-hidden="true" className="size-4" />
                          </IconButton>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
                {confirming ? (
                  <p className="mt-1 text-xs text-ink-muted">
                    {t('labelPicker.deleteWarning')}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Popover>
  );
}
